import React from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { fmtMoney } from "@/lib/api";
import { Bookmark, Sparkles, Tag, Clock } from "lucide-react";

export default function DealCard({ offer, onSave, onShop, onExplain, saved }) {
  const expiringSoon = offer?.expiration && new Date(offer.expiration) - new Date() < 1000 * 60 * 60 * 24 * 7;
  return (
    <div
      className="group relative bg-white border border-gray-200 rounded-2xl p-5 hover:-translate-y-1 hover:shadow-card transition-all duration-200 flex flex-col"
      data-testid={`deal-card-${offer.id}`}
    >
      <div className="flex items-start gap-3 mb-3">
        {offer.merchant_logo ? (
          <img
            src={offer.merchant_logo}
            alt={offer.merchant_name}
            className="w-11 h-11 rounded-xl object-contain bg-gray-50 border border-gray-100 p-1"
            onError={(e) => { e.target.style.display = "none"; }}
          />
        ) : (
          <div className="w-11 h-11 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center font-bold">
            {offer.merchant_name?.[0]}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-[0.18em] text-gray-500 font-medium">
            {offer.merchant_name}
          </div>
          <h3 className="font-display font-bold text-base text-gray-900 leading-snug mt-0.5 line-clamp-2">
            {offer.title}
          </h3>
        </div>
        <button
          onClick={() => onSave?.(offer)}
          data-testid={`save-${offer.id}`}
          className={`p-2 rounded-lg transition-colors ${saved ? "text-amber-600 bg-amber-50" : "text-gray-400 hover:text-amber-600 hover:bg-amber-50"}`}
          aria-label="Save deal"
        >
          <Bookmark className="w-4 h-4" fill={saved ? "currentColor" : "none"} />
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-3">
        <Badge className="bg-emerald-50 text-emerald-800 hover:bg-emerald-50 border border-emerald-200 font-semibold">
          {offer.cashback_rate}% cashback
        </Badge>
        {offer.coupon_code && (
          <Badge className="bg-amber-50 text-amber-800 hover:bg-amber-50 border border-amber-200">
            <Tag className="w-3 h-3 mr-1" /> {offer.coupon_code}
          </Badge>
        )}
        {offer.featured && (
          <Badge className="bg-sky-50 text-sky-800 hover:bg-sky-50 border border-sky-200">
            <Sparkles className="w-3 h-3 mr-1" /> Featured
          </Badge>
        )}
        {expiringSoon && (
          <Badge className="bg-rose-50 text-rose-800 hover:bg-rose-50 border border-rose-200">
            <Clock className="w-3 h-3 mr-1" /> Expiring
          </Badge>
        )}
      </div>

      {offer.description && (
        <p className="text-sm text-gray-600 line-clamp-2 mb-4 flex-1">{offer.description}</p>
      )}

      <div className="flex gap-2 mt-auto">
        <Link
          to={`/app/deals/${offer.id}`}
          className="text-xs text-gray-500 hover:text-emerald-700 flex items-center"
          data-testid={`view-${offer.id}`}
        >
          View details →
        </Link>
        <div className="flex-1" />
        <Button
          size="sm"
          onClick={() => onShop?.(offer)}
          data-testid={`shop-${offer.id}`}
          className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-full px-4"
        >
          Shop now
        </Button>
      </div>
    </div>
  );
}
